import { ChangeDetectionStrategy, Component, computed, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-aula3-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      <!-- Breadcrumb Navigation & Top Action Bar -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <button 
          type="button" 
          (click)="navigateBack.emit()" 
          class="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-[#F27D26] border border-white/10 transition-all cursor-pointer">
          <span class="material-symbols-outlined text-base">arrow_back</span>
          <span>Voltar ao Menu de Aulas</span>
        </button>

        <div class="flex flex-wrap items-center gap-3">
          <!-- View Switcher Tabs -->
          <div class="flex items-center space-x-1 bg-[#121214] p-1.5 rounded-xl border border-white/10">
            <button 
              type="button"
              (click)="onSelectView('slides')"
              [class]="activeView() === 'slides' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">slideshow</span>
              <span>Slides (12 Slides)</span>
            </button>

            <button 
              type="button"
              (click)="onSelectView('activity')"
              [class]="activeView() === 'activity' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">assignment</span>
              <span>Folha de Atividades Completa</span>
            </button>
          </div>
        </div>
      </div>

      <!-- VIEW 1: INLINE SLIDES PRESENTATION CARD -->
      @if (activeView() === 'slides') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 min-h-[580px] flex flex-col justify-between relative overflow-hidden">
          <!-- Slide Glow Accent -->
          <div class="absolute -top-24 -right-24 w-80 h-80 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

          <!-- Top Banner Bar -->
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-white/10">
            <div class="flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26] text-xl">slideshow</span>
              <span class="text-xs font-mono font-semibold text-zinc-300">Modo de Leitura Integrado</span>
            </div>
            
            <button 
              type="button" 
              (click)="openPresentationModal()" 
              class="px-3 py-1.5 rounded-lg bg-[#F27D26]/15 hover:bg-[#F27D26]/25 text-[#F27D26] border border-[#F27D26]/30 text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-sm">present_to_all</span>
              <span>Apresentação Imersiva</span>
            </button>
          </div>

          <!-- SLIDES CONTENT -->
          <div class="my-6">
            <!-- SLIDE 1 -->
            @if (currentSlide() === 0) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">psychology</span>
                  <span>AULA 3 • COMPORTAMENTO EMPREENDEDOR</span>
                </span>

                <div>
                  <h1 class="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                    O Perfil que Transforma Redes em Sucesso
                  </h1>
                  <p class="text-lg text-zinc-400 font-medium mt-1">
                    Dominando as 10 Características do Comportamento Empreendedor (CCEs).
                  </p>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3">
                  <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                    <span>🙋‍♂️</span>
                    <span>A Jornada do João (Novo Desafio):</span>
                  </h3>
                  <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                    Na Aula 2, o João instalou o Link Duplo e organizou o cabeamento da <em>Logística Expressa</em>. A rede ficou perfeita e os caminhões nunca mais atrasaram! Uma semana após a entrega, o <strong>Sr. Roberto procurou o João muito satisfeito</strong>, mas trouxe uma nova preocupação de negócio: <em class="text-amber-400 font-semibold">"João, a internet está ótima, mas e se nosso servidor em nuvem travar no meio da noite ou queimar um switch no fim de semana? Quem monitora isso para nós não pararmos?"</em>
                  </p>
                </div>

                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  O cliente quer <strong>prevenção e tranquilidade contínua</strong>. Para transformar esse momento em um contrato mensal de longo prazo, o João precisará desenvolver seu <strong>Comportamento Empreendedor</strong>!
                </p>
              </div>
            }

            <!-- SLIDE 2 -->
            @if (currentSlide() === 1) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">construction</span>
                  <span>PASSO 1 • CONCEITOS FUNDAMENTAIS</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Habilidades Técnicas vs. Habilidades Comportamentais
                </h2>

                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  No mercado de TIC, todo profissional é avaliado sob duas perspectivas essenciais:
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3">
                    <h3 class="text-base font-bold text-sky-400 flex items-center space-x-2">
                      <span>🛠️</span>
                      <span>1. Habilidades Técnicas (Hard Skills)</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>Configurar VLANs e roteadores MikroTik.</li>
                      <li>Montar e certificar cabeamento estruturado (Cat6/Fibra).</li>
                      <li>Implantar servidores Linux e regras de Firewall.</li>
                      <li class="text-sky-300 font-semibold"><strong>Papel:</strong> É a base técnica que qualifica você para atuar.</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3">
                    <h3 class="text-base font-bold text-amber-400 flex items-center space-x-2">
                      <span>🧠</span>
                      <span>2. Habilidades Comportamentais (Soft Skills)</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>Cumprir prazos com rigor e ter iniciativa própria.</li>
                      <li>Ouvir as necessidades do cliente sem impor jargões técnicos.</li>
                      <li>Manter o equilíbrio emocional e resolver crises com agilidade.</li>
                      <li class="text-amber-300 font-semibold"><strong>Papel:</strong> É o que constrói reputação, contratos duradouros e fidelidade.</li>
                    </ul>
                  </div>
                </div>

                <div class="bg-[#F27D26]/10 rounded-xl p-4 border border-[#F27D26]/30 text-center font-mono text-xs sm:text-sm font-semibold text-[#F27D26] leading-relaxed">
                  REGRA DO MERCADO DE TI: O conhecimento técnico abre a porta, mas o comportamento empreendedor mantém a porta aberta! 🔑
                </div>
              </div>
            }

            <!-- SLIDE 3 -->
            @if (currentSlide() === 2) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">science</span>
                  <span>PASSO 2 • METODOLOGIA CIENTÍFICA</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O que é CCE? A Metodologia McClelland (Sebrae/ONU)
                </h2>

                <p class="text-zinc-300 text-sm leading-relaxed text-justify">
                  A sigla <strong>CCE</strong> significa <strong>Características do Comportamento Empreendedor</strong>.
                </p>

                <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
                  <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
                    Na década de 1980, o psicólogo <strong>David McClelland</strong> (da Universidade de Harvard), em parceria com a <strong>ONU</strong> (e aplicada no Brasil pelo <strong>SEBRAE</strong> em programas como Empretec e Despertar), estudou empreendedores de sucesso em mais de 30 países para descobrir o segredo do sucesso nos negócios.
                  </p>
                </div>

                <div class="bg-emerald-950/40 rounded-xl p-5 border border-emerald-500/30 space-y-2">
                  <h3 class="text-base font-bold text-emerald-300 flex items-center space-x-2">
                    <span class="material-symbols-outlined text-emerald-400 text-lg">verified</span>
                    <span>A Grande Descoberta Científica:</span>
                  </h3>
                  <p class="text-zinc-200 text-xs sm:text-sm leading-relaxed text-justify">
                    Empreendedorismo <strong>NÃO é sorte nem dom divino de nascimento!</strong> Trata-se de um conjunto de <strong>10 comportamentos observáveis e mensuráveis</strong> que qualquer profissional pode treinar e aprimorar para alcançar excelência em sua carreira técnica.
                  </p>
                </div>
              </div>
            }

            <!-- SLIDE 4 -->
            @if (currentSlide() === 3) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">grid_view</span>
                  <span>PASSO 3 • O MAPA GERAL DAS 10 CCES</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Os 3 Conjuntos do Comportamento Empreendedor
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-2">
                    <div class="text-2xl">⚡</div>
                    <h3 class="font-bold text-[#F27D26] text-sm">1. Conjunto de Realização</h3>
                    <ul class="space-y-1 text-xs text-zinc-300 font-mono">
                      <li>• CCE 1: Busca de Oportunidades</li>
                      <li>• CCE 2: Riscos Calculados</li>
                      <li>• CCE 3: Qualidade & Eficiência</li>
                      <li>• CCE 4: Persistência</li>
                      <li>• CCE 5: Comprometimento</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-2">
                    <div class="text-2xl">🎯</div>
                    <h3 class="font-bold text-sky-400 text-sm">2. Conjunto de Planejamento</h3>
                    <ul class="space-y-1 text-xs text-zinc-300 font-mono">
                      <li>• CCE 6: Busca de Informações</li>
                      <li>• CCE 7: Estabelecimento de Metas</li>
                      <li>• CCE 8: Planejamento e Monitoramento Sistemáticos</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-2">
                    <div class="text-2xl">🤝</div>
                    <h3 class="font-bold text-purple-400 text-sm">3. Conjunto de Poder</h3>
                    <ul class="space-y-1 text-xs text-zinc-300 font-mono">
                      <li>• CCE 9: Persuasão & Networking</li>
                      <li>• CCE 10: Autoconfiança & Independência</li>
                    </ul>
                  </div>
                </div>

                <div class="bg-indigo-950/40 rounded-xl p-3 border border-indigo-500/30 text-xs text-zinc-300 text-center font-mono">
                  💡 A seguir, veja como aplicar cada uma das 10 CCEs no dia a dia de Redes de Computadores!
                </div>
              </div>
            }

            <!-- SLIDE 5 -->
            @if (currentSlide() === 4) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">bolt</span>
                  <span>CONJUNTO 1 • BLOCO DE REALIZAÇÃO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  As 5 CCEs de Realização em Redes
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <span class="px-2 py-0.5 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold">CCE 1</span>
                    <h3 class="font-bold text-white">Busca de Oportunidades e Iniciativa</h3>
                    <p class="text-zinc-400 text-justify">Agir antes de ser mandado. Ex: Identificar que comércios do bairro precisam de Wi-Fi para clientes e criar pacotes de Hotspot seguro.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <span class="px-2 py-0.5 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold">CCE 2</span>
                    <h3 class="font-bold text-white">Correr Riscos Calculados</h3>
                    <p class="text-zinc-400 text-justify">Avaliar alternativas e reduzir perdas. Ex: Fazer backup de segurança antes de atualizar o sistema de um roteador corporativo.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <span class="px-2 py-0.5 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold">CCE 3</span>
                    <h3 class="font-bold text-white">Exigência de Qualidade e Eficiência</h3>
                    <p class="text-zinc-400 text-justify">Superar padrões normais. Ex: Identificar cabos com anilhas numeradas, organizar o rack e certificar todos os pontos com Fluke tester.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <span class="px-2 py-0.5 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold">CCE 4 & 5</span>
                    <h3 class="font-bold text-white">Persistência & Comprometimento</h3>
                    <p class="text-zinc-400 text-justify">Analisar logs pacientemente até achar perdas intermitentes (CCE 4) e assumir a responsabilidade pessoal de restabelecer o sinal rompido (CCE 5).</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 6 -->
            @if (currentSlide() === 5) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">timeline</span>
                  <span>CONJUNTO 2 • BLOCO DE PLANEJAMENTO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  As 3 CCEs de Planejamento em Redes
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-sky-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 font-mono font-bold">CCE 6</span>
                    <h3 class="font-bold text-white text-sm">Busca de Informações</h3>
                    <p class="text-zinc-400 leading-relaxed text-justify">Pesquisar a fundo relatórios de segurança da Anatel/Cert.br, consultar documentações técnicas de fabricantes e mapear o tráfego do cliente antes de orçar.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-sky-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 font-mono font-bold">CCE 7</span>
                    <h3 class="font-bold text-white text-sm">Estabelecimento de Metas</h3>
                    <p class="text-zinc-400 leading-relaxed text-justify">Definir metas claras e mensuráveis. Ex: <em>"Vou conquistar 8 contratos de suporte gerenciado (MSP) em 6 meses, faturando R$ 5.200,00 mensais."</em></p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-sky-500/30 space-y-2">
                    <span class="px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 font-mono font-bold">CCE 8</span>
                    <h3 class="font-bold text-white text-sm">Planejamento & Monitoramento</h3>
                    <p class="text-zinc-400 leading-relaxed text-justify">Dividir implantações grandes em etapas no Kanban, acompanhar cronogramas e monitorar a disponibilidade (SLA 99.5%) em painéis do Zabbix.</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 7 -->
            @if (currentSlide() === 6) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">handshake</span>
                  <span>CONJUNTO 3 • BLOCO DE PODER</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  As 2 CCEs de Poder e Relacionamento em TIC
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-purple-500/30 space-y-3">
                    <span class="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-mono font-bold text-xs">CCE 9</span>
                    <h3 class="text-base font-bold text-white flex items-center space-x-2">
                      <span>🗣️</span>
                      <span>Persuasão e Rede de Contatos (Networking)</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>Saber explicar conceitos (Failover, VLAN, SLA) em linguagem simples de negócios.</li>
                      <li>Cultivar bom relacionamento com professores, colegas e empresários locais.</li>
                      <li class="text-purple-300 font-semibold">Fato de mercado: Mais de 75% dos contratos em TI surgem por indicação direta!</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-purple-500/30 space-y-3">
                    <span class="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-mono font-bold text-xs">CCE 10</span>
                    <h3 class="text-base font-bold text-white flex items-center space-x-2">
                      <span>🦁</span>
                      <span>Independência e Autoconfiança</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>Confiar no seu conhecimento técnico e saber precificar seu trabalho de forma justa.</li>
                      <li>Não aceitar valores humilhantes por medo de perder o serviço.</li>
                      <li class="text-purple-300 font-semibold">Manter sua recomendação de segurança mesmo quando o cliente pede improvisos arriscados.</li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 8 -->
            @if (currentSlide() === 7) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">cases</span>
                  <span>ESTUDO DE CASO PRÁTICO • APLICAÇÃO REAL</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Como o João Respondeu ao Sr. Roberto
                </h2>

                <div class="bg-[#0A0A0B] rounded-xl p-4 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                  <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
                    Veja como o João utilizou as CCEs para transformar a dúvida do Sr. Roberto sobre falhas noturnas em uma oportunidade comercial:
                  </p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <h3 class="font-bold text-[#F27D26]">1. Busca de Informações & Planejamento (CCE 6 e 8)</h3>
                    <p class="text-zinc-400 text-justify">João analisou o fluxo da Logística Expressa e configurou um painel Zabbix com alertas automáticos no Telegram para avisar antes do servidor travar.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <h3 class="font-bold text-[#F27D26]">2. Exigência de Qualidade & Comprometimento (CCE 3 e 5)</h3>
                    <p class="text-zinc-400 text-justify">Definiu um Acordo de Nível de Serviço (SLA) por escrito: caso ocorra falha crítica, o atendimento presencial é garantido em até 40 minutos.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <h3 class="font-bold text-[#F27D26]">3. Iniciativa & Riscos Calculados (CCE 1 e 2)</h3>
                    <p class="text-zinc-400 text-justify">Estruturou rotinas automatizadas de backup diário na nuvem com testes semanais de restauração para garantir zero perda de dados.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-3.5 border border-white/10 space-y-1">
                    <h3 class="font-bold text-[#F27D26]">4. Persuasão & Autoconfiança (CCE 9 e 10)</h3>
                    <p class="text-zinc-400 text-justify">Apresentou a proposta de <strong>Contrato Mensal MSP por R$ 650/mês</strong>, demonstrando que o valor garante a operação contínua da empresa.</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 9 -->
            @if (currentSlide() === 8) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">autorenew</span>
                  <span>MODELO DE NEGÓCIO • SUSTENTABILIDADE</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Do "Técnico Apaga-Fogo" ao Negócio MSP
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-red-500/40 space-y-3">
                    <h3 class="text-base font-bold text-red-400 flex items-center space-x-2">
                      <span>🔥</span>
                      <span>Modelo Antigo ("Quebra-Paga" / Apaga-Fogo)</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>Você só ganha dinheiro quando o cliente está em desespero e prejuízo.</li>
                      <li>Renda instável: num mês fatura R$ 2.000, no outro apenas R$ 300.</li>
                      <li>Você passa o dia correndo atrás de chamados desorganizados.</li>
                      <li>O cliente enxerga você como um "custo chato".</li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-emerald-500/40 space-y-3">
                    <h3 class="text-base font-bold text-emerald-400 flex items-center space-x-2">
                      <span>💎</span>
                      <span>Modelo MSP (Serviços Gerenciados com SLA)</span>
                    </h3>
                    <ul class="space-y-2 text-xs text-zinc-300 pl-4 list-disc text-justify">
                      <li>O cliente paga uma mensalidade fixa para você <strong>prevenir falhas</strong>.</li>
                      <li>Renda previsível garantida todo dia 5 do mês.</li>
                      <li>Ferramentas automatizadas para monitorar múltiplos clientes ao mesmo tempo.</li>
                      <li>O cliente enxerga você como um parceiro estratégico essencial.</li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 10 -->
            @if (currentSlide() === 9) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">trending_up</span>
                  <span>METAS FINANCEIRAS • ESCALABILIDADE</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Matemática da Liberdade Financeira em TIC
                </h2>

                <div class="bg-[#F27D26]/10 rounded-xl p-3 border border-[#F27D26]/30 text-center font-mono text-xs sm:text-sm font-semibold text-[#F27D26]">
                  MENSALIDADE POR CONTRATO (R$ 650,00) × QUANTIDADE DE CLIENTES = RECURSO RECORRENTE 📈
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-2">
                    <h3 class="text-xs font-bold text-zinc-400">Fase 1: Início (1 a 3 meses)</h3>
                    <p class="text-xl font-bold text-[#F27D26]">3 Contratos</p>
                    <p class="text-xs font-bold text-white">R$ 1.950,00 / mês</p>
                    <p class="text-[11px] text-zinc-400">Cobre seus custos de transporte, internet e ferramentas.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-[#F27D26]/50 space-y-2 shadow-lg shadow-[#F27D26]/10">
                    <h3 class="text-xs font-bold text-[#F27D26]">Fase 2: Consolidação (6 meses)</h3>
                    <p class="text-xl font-bold text-white">8 Contratos</p>
                    <p class="text-xs font-bold text-[#F27D26]">R$ 5.200,00 / mês</p>
                    <p class="text-[11px] text-zinc-300">Renda superior ao piso salarial médio de técnicos contratados.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-emerald-500/40 space-y-2">
                    <h3 class="text-xs font-bold text-emerald-400">Fase 3: Expansão (12 meses)</h3>
                    <p class="text-xl font-bold text-emerald-400">15 Contratos</p>
                    <p class="text-xs font-bold text-white">R$ 9.750,00 / mês</p>
                    <p class="text-[11px] text-zinc-400">Hora de contratar um estagiário de redes e abrir escritório!</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 11 -->
            @if (currentSlide() === 10) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">forum</span>
                  <span>PERSUASÃO • NEGOCIAÇÃO DE VALOR</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Como Vencer as Objeções dos Clientes
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
                    <h3 class="text-xs sm:text-sm font-bold text-red-400">Objeção 1: <em>"Se a rede não der defeito no mês, vou pagar sem usar?"</em></h3>
                    <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                      <strong>Resposta Empreendedora:</strong><br>
                      <em>"Sr. Roberto, pense no plano de saúde ou no seguro do carro: pagamos torcendo para não usar! Na TI, a ausência de problemas é o resultado do nosso monitoramento diário e limpezas preventivas, garantindo que o senhor nunca perca R$ 1.500/hora em paralisações."</em>
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
                    <h3 class="text-xs sm:text-sm font-bold text-red-400">Objeção 2: <em>"Achei R$ 650 caro, faz por R$ 200?"</em></h3>
                    <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                      <strong>Resposta Empreendedora (Autoconfiança CCE 10):</strong><br>
                      <em>"Entendo sua preocupação de custos. Porém, por R$ 200 não é possível manter servidor de monitoramento 24h na nuvem nem garantir SLA de atendimento presencial em 40 minutos por contrato. Nosso valor garante a segurança total da sua empresa."</em>
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 12 -->
            @if (currentSlide() === 11) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">verified</span>
                  <span>SÍNTESE DA AULA</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Grande Lição do Dia
                </h2>

                <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-8 border border-[#F27D26]/40 text-center">
                  <h3 class="text-lg sm:text-xl font-extrabold text-[#F27D26] leading-relaxed">
                    "O conhecimento técnico em redes ensina você a configurar switches e servidores.<br><br>As Características do Comportamento Empreendedor (CCEs) ensinam você a ser dono do seu próprio destino e construir uma carreira de respeito."
                  </h3>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-3">
                  <h3 class="text-base font-bold text-white flex items-center space-x-2">
                    <span>🚀</span>
                    <span>Próximo Passo:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify">
                    Abra a <strong>Folha de Atividades</strong>, realize o Teste de Autoavaliação Oficial das 10 CCEs (com pontuação automática Sebrae/ONU), calcule seu nível empreendedor, estruture o MSP e enfrente o desafio real da Brisanet no Módulo E!
                  </p>
                  <div class="pt-2">
                    <button 
                      type="button" 
                      (click)="onSelectView('activity')" 
                      class="px-4 py-2 rounded-lg bg-[#F27D26] text-black font-bold text-xs hover:bg-[#e06c17] transition-all flex items-center space-x-1.5 cursor-pointer">
                      <span class="material-symbols-outlined text-base">assignment</span>
                      <span>Ir para Folha de Atividades Completa</span>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Slide Controls Footer -->
          <div class="pt-4 border-t border-white/10 flex items-center justify-between">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span>Anterior</span>
            </button>

            <div class="flex items-center space-x-3">
              <span class="text-xs text-zinc-400 font-mono font-medium">
                Slide {{ currentSlide() + 1 }} de {{ totalSlides }}
              </span>
              <div class="w-24 sm:w-36 h-2 bg-white/10 rounded-full overflow-hidden hidden sm:block">
                <div 
                  class="h-full bg-[#F27D26] transition-all duration-300"
                  [style.width.%]="((currentSlide() + 1) / totalSlides) * 100">
                </div>
              </div>
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-4 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer">
              <span>Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }

      <!-- VIEW 2: FOLHA DE ATIVIDADES COMPLETA -->
      @if (activeView() === 'activity') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 space-y-8">
          <!-- Activity Header -->
          <div class="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
            <div>
              <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider mb-2">
                <span class="material-symbols-outlined text-sm">edit_note</span>
                <span>DIAGNÓSTICO & EXERCÍCIO COMPORTAMENTAL</span>
              </span>
              <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                📝 Folha de Atividades & Diagnóstico das 10 CCEs
              </h1>
            </div>

            <button 
              type="button" 
              (click)="printActivity()" 
              class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/20 transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">print</span>
              <span>Imprimir Folha</span>
            </button>
          </div>

          <!-- MÓDULO A -->
          <div class="space-y-4">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO A: Domínio e Fixação dos 3 Conjuntos das CCEs</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              <strong>1. Associe cada uma das 10 CCEs (metodologia McClelland/Sebrae/ONU) ao seu respectivo Conjunto Estratégico:</strong><br>
              Opções: <strong class="text-[#F27D26]">[ R ] Realização</strong> | <strong class="text-sky-400">[ P ] Planejamento</strong> | <strong class="text-purple-400">[ POD ] Poder</strong>
            </p>

            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs border-collapse border border-white/10 rounded-xl overflow-hidden">
                <thead>
                  <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                    <th class="p-3 border-r border-white/10 text-center w-20">Conjunto</th>
                    <th class="p-3 border-r border-white/10 w-1/3">Característica do Comportamento Empreendedor (CCE)</th>
                    <th class="p-3">Exemplo Prático no Trabalho de Redes</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/10 text-zinc-300">
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[0]" placeholder="R/P/POD" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-[#F27D26] focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">1. Busca de Oportunidades e Iniciativa</td>
                    <td class="p-3 text-justify">Identifica que empresas locais não possuem Wi-Fi isolado para clientes e oferece a solução antes de ser chamado.</td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[1]" placeholder="R/P/POD" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-[#F27D26] focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">2. Correr Riscos Calculados</td>
                    <td class="p-3 text-justify">Realiza backup das configurações e agenda atualizações críticas de firewall para horários de menor tráfego.</td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[2]" placeholder="R/P/POD" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-[#F27D26] focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">3. Exigência de Qualidade e Eficiência</td>
                    <td class="p-3 text-justify">Padroniza a identificação de cabos com etiquetas térmicas e certifica todas as portas do Patch Panel.</td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[3]" placeholder="R/P/POD" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-[#F27D26] focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">4. Persistência</td>
                    <td class="p-3 text-justify">Investiga pacotes de rede e logs de roteadores repetidamente até descobrir a causa de uma perda intermitente de sinal.</td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[4]" placeholder="R/P/POD" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-[#F27D26] focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">5. Comprometimento</td>
                    <td class="p-3 text-justify">Assume pessoalmente a responsabilidade por restabelecer a infraestrutura caso ocorra uma falha técnica grave.</td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[5]" placeholder="R/P/POD" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-sky-400 focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">6. Busca de Informações</td>
                    <td class="p-3 text-justify">Consulta documentações de fabricantes, relatórios de segurança da Anatel/Cert.br e fóruns especializados.</td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[6]" placeholder="R/P/POD" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-sky-400 focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">7. Estabelecimento de Metas</td>
                    <td class="p-3 text-justify">Define o objetivo claro de conquistar 8 contratos de suporte mensal (MSP) no prazo de 6 meses.</td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[7]" placeholder="R/P/POD" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-sky-400 focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">8. Planejamento e Monitoramento Sistemáticos</td>
                    <td class="p-3 text-justify">Utiliza ferramentas de Kanban e dashboards no Zabbix para acompanhar o tempo de atendimento de chamados.</td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[8]" placeholder="R/P/POD" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-purple-400 focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">9. Persuasão e Rede de Contatos</td>
                    <td class="p-3 text-justify">Explica a importância do investimento em cibersegurança em termos financeiros de prevenção de prejuízos.</td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-center">
                      <input type="text" [(ngModel)]="modA_assoc[9]" placeholder="R/P/POD" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-purple-400 focus:outline-none">
                    </td>
                    <td class="p-3 border-r border-white/10 font-bold text-white">10. Independência e Autoconfiança</td>
                    <td class="p-3 text-justify">Mantém seu posicionamento técnico de segurança mesmo quando o cliente pede para desligar senhas por comodidade.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- MÓDULO B -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO B: Diagnóstico de Perfil Empreendedor (Escala Oficial Sebrae/ONU)</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              Atribua notas de <strong>1 (Nunca / Raramente)</strong> a <strong>5 (Sempre / Constantemente)</strong> para cada afirmação comportamental. A pontuação total será calculada automaticamente:
            </p>

            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs border-collapse border border-white/10 rounded-xl overflow-hidden">
                <thead>
                  <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                    <th class="p-3 border-r border-white/10">Afirmação Comportamental (Base UNCTAD / ONU / Sebrae)</th>
                    <th class="p-3 text-center w-28">Nota (1 a 5)</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/10 text-zinc-300">
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-justify">1. Tomo iniciativa para estudar novas tecnologias e resolver problemas sem esperar ordem de terceiros. (Iniciativa)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q1" (ngModelChange)="recalcTotal()" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-justify">2. Antes de tomar decisões arriscadas, avalio alternativas e crio planos de contingência/backup. (Riscos Calculados)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q2" (ngModelChange)="recalcTotal()" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-justify">3. Busco fazer minhas tarefas com excelência, organização e dentro dos prazos combinados. (Qualidade e Eficiência)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q3" (ngModelChange)="recalcTotal()" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-justify">4. Não desisto diante de configurações ou erros complexos; testo diferentes saídas até encontrar a solução. (Persistência)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q4" (ngModelChange)="recalcTotal()" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-justify">5. Cumpro rigorosamente o que prometo a colegas e professores, mesmo que exija esforço extra. (Comprometimento)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q5" (ngModelChange)="recalcTotal()" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-justify">6. Pesquiso em manuais, livros e consulto especialistas quando preciso aprender algo novo. (Busca de Informações)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q6" (ngModelChange)="recalcTotal()" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-justify">7. Estabeleço metas desafiadoras para meus estudos e minha carreira com datas limites para conclusão. (Metas)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q7" (ngModelChange)="recalcTotal()" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-justify">8. Divido projetos grandes em pequenas etapas e acompanho o progresso diariamente. (Planejamento)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q8" (ngModelChange)="recalcTotal()" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 text-justify">9. Consigo convencer pessoas sobre minhas ideias e mantenho uma boa rede de relacionamentos. (Persuasão / Networking)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q9" (ngModelChange)="recalcTotal()" class="w-16 bg-[#0A0A0B] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 text-justify">10. Confio na minha capacidade técnica e defendo meu trabalho com postura firme e segura. (Autoconfiança)</td>
                    <td class="p-3 text-center">
                      <input type="number" min="1" max="5" [(ngModel)]="cceScores.q10" (ngModelChange)="recalcTotal()" class="w-16 bg-[#121214] border border-white/10 rounded p-1 text-center font-mono font-bold text-xs text-white focus:outline-none focus:border-[#F27D26]">
                    </td>
                  </tr>
                  <tr class="bg-[#121214] font-bold">
                    <td class="p-3 border-r border-white/10 text-right text-[#F27D26]">PONTUAÇÃO TOTAL ACUMULADA:</td>
                    <td class="p-3 text-center font-mono text-sm text-[#F27D26]">
                      {{ totalScore() }} / 50
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- RESULTADO E ENQUADRAMENTO ESTATÍSTICO -->
            <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3">
              <h3 class="text-sm font-bold text-emerald-400 flex items-center space-x-2">
                <span>📊</span>
                <span>Enquadramento do Perfil Empreendedor (Tabela Oficial Sebrae/ONU):</span>
              </h3>

              <div class="space-y-2 text-xs text-zinc-300">
                <div [class.font-bold]="totalScore() <= 25" [class.text-[#F27D26]]="totalScore() <= 25">
                  • <strong>10 a 25 Pontos (Nível Inicial / Em Desenvolvimento):</strong> Postura predominantemente reativa. É necessário exercitar a iniciativa, o planejamento e a persistência diante de dificuldades.
                </div>
                <div [class.font-bold]="totalScore() >= 26 && totalScore() <= 39" [class.text-sky-400]="totalScore() >= 26 && totalScore() <= 39">
                  • <strong>26 a 39 Pontos (Nível Intermediário / Competitivo):</strong> Demonstra boas atitudes empreendedoras na maioria das situações, mas precisa fortalecer o estabelecimento de metas claras e o networking estratégico.
                </div>
                <div [class.font-bold]="totalScore() >= 40" [class.text-emerald-400]="totalScore() >= 40">
                  • <strong>40 a 50 Pontos (Nível Empreendedor de Alta Performance):</strong> Perfil proativo, planejado e seguro. Pratica os comportamentos essenciais para gerenciar negócios em tecnologia ou liderar equipes de TIC.
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>2. Autoanálise Crítica:</strong> Em qual faixa de pontuação você se enquadrou? Quais foram as <strong>duas CCEs com menor pontuação</strong> no seu teste e que atitude concreta você adotará para desenvolvê-las durante o curso?
              </label>
              <textarea [(ngModel)]="modB_autoanalise" rows="3" placeholder="Digite sua reflexão aqui..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>
          </div>

          <!-- MÓDULO C -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO C: Estudo de Caso – Estruturação do Negócio MSP do João</span>
            </h2>

            <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
              <h3 class="text-base font-bold text-white flex items-center space-x-2">
                <span class="material-symbols-outlined text-[#F27D26] text-lg">description</span>
                <span>Proposta de Contrato de Suporte Recorrente</span>
              </h3>
              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                Após o Sr. Roberto perguntar quem cuidaria da rede da Logística Expressa em caso de falhas noturnas, o João apresentou uma proposta de <strong>Serviços Gerenciados (MSP)</strong> no valor de <strong>R$ 650,00 mensais</strong>, contemplando monitoramento proativo de links via Zabbix, rotina automática de backup na nuvem e garantia contratual de atendimento em no máximo 40 minutos em caso de incidente crítico.
              </p>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>3. Identificação Comportamental:</strong> Aponte <strong>duas CCEs</strong> que o João utilizou ao estruturar essa proposta mensal em vez de apenas esperar o cliente chamá-lo no próximo defeito:
              </label>
              <textarea [(ngModel)]="modC_q3" rows="2" placeholder="Sua resposta..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>

            <div class="space-y-3">
              <label class="block text-xs font-bold text-zinc-200 text-justify">
                <strong>4. Simulação Financeira e Escalabilidade (CCE 7 e 8):</strong><br>
                Imagine que você e seu grupo decidiram abrir uma consultoria de suporte gerenciado de redes. Responda:
              </label>

              <div class="space-y-3 text-xs">
                <div>
                  <p class="text-zinc-300 mb-1"><strong>a)</strong> Se vocês conquistarem <strong>10 contratos mensais</strong> no valor de R$ 650,00/mês, qual será o faturamento bruto mensal da empresa?</p>
                  <input type="text" [(ngModel)]="modC_q4_a" placeholder="Cálculo do faturamento..." class="w-full bg-[#0A0A0B] border border-white/10 rounded p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                </div>

                <div>
                  <p class="text-zinc-300 mb-1"><strong>b)</strong> Considerando que o custo mensal de servidores em nuvem (Zabbix + Backup) para atender esses 10 clientes seja de R$ 450,00 no total, qual será o <strong>lucro líquido mensal</strong> da equipe?</p>
                  <input type="text" [(ngModel)]="modC_q4_b" placeholder="Cálculo do lucro líquido..." class="w-full bg-[#0A0A0B] border border-white/10 rounded p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                </div>
              </div>
            </div>
          </div>

          <!-- MÓDULO D -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO D: Simulação de Negociação e Quebra de Objeções (Roleplay)</span>
            </h2>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-2 text-justify">
                <strong>5. Desafio de Persuasão (CCE 9 e 10):</strong><br>
                Durante a reunião de apresentação, o Sr. Roberto argumenta: <em class="text-amber-400">"João, se a minha rede e meus servidores passarem dois meses sem dar nenhum defeito, por que eu deveria continuar te pagando esses R$ 650 todo mês? Isso não é um gasto desnecessário?"</em>
              </label>

              <div class="bg-indigo-950/40 rounded-xl p-4 border border-indigo-500/30 space-y-2 mb-3">
                <h3 class="text-xs font-bold text-indigo-300 flex items-center space-x-1.5">
                  <span class="material-symbols-outlined text-sm">lightbulb</span>
                  <span>💡 Roteiro de Persuasão Empreendedora:</span>
                </h3>
                <p class="text-xs text-zinc-300 text-justify">Construa sua resposta em 3 partes: <strong>1.</strong> Agradeça a pergunta e use a analogia de seguros/manutenção preventiva; <strong>2.</strong> Explique que a falta de defeitos é a prova do trabalho de monitoramento diário nos bastidores; <strong>3.</strong> Relembre que o valor mensal é muito menor que uma única hora de parada da logística (R$ 1.500/h).</p>
              </div>

              <textarea [(ngModel)]="modD_pitch" rows="5" placeholder="Escreva a argumentação comercial da sua equipe aqui..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>
          </div>

          <!-- MÓDULO E -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO E: Caso Real Interativo – O Desafio da Conectividade Regional (RPG Empreendedor Brisanet)</span>
            </h2>

            <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
              <h3 class="text-base font-bold text-white flex items-center space-x-2">
                <span class="material-symbols-outlined text-[#F27D26] text-lg">history_edu</span>
                <span>Contexto Histórico: O Desafio de Conectar o Interior</span>
              </h3>
              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                Em 1998, no município de Pereiro (interior do Ceará), um empreendedor enfrentava uma realidade comum no Brasil profundo: as grandes operadoras multinacionais afirmavam que cidades do interior eram inviáveis economicamente e se recusavam a instalar linhas telefônicas e infraestrutura de internet. Esse cenário deu origem a um dos maiores provedores regionais do país (a Brisanet).
              </p>
              <p class="text-xs text-zinc-300 leading-relaxed font-semibold text-[#F27D26] text-justify">
                Seu Desafio: Analisem os 3 dilemas reais enfrentados pelo negócio ao longo da sua história. Escolham a decisão que a SUA equipe tomaria em cada fase e justifiquem os comportamentos empreendedores (CCEs) necessários.
              </p>
            </div>

            <!-- DILEMA 1 -->
            <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3">
              <h3 class="text-sm font-bold text-sky-400">🎮 DILEMA 1: O Vazio de Infraestrutura (Ano de 1998)</h3>
              <p class="text-xs text-zinc-300 text-justify"><strong>Cenário:</strong> A cidade não tem cabeamento nas ruas. A população deseja acesso à rede, mas nenhuma grande empresa aceita investir na infraestrutura local.</p>

              <div class="space-y-2 text-xs text-zinc-300">
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema1" value="A" [(ngModel)]="modE_dilema1" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção A:</strong> Abrir um comércio tradicional na cidade e aguardar que uma grande operadora ou o governo resolva o problema da telecomunicação.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema1" value="B" [(ngModel)]="modE_dilema1" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção B:</strong> Desenvolver antenas e transmissores de rádio artesanais adaptados, transmitindo o sinal de internet pelo ar (rede sem fio/WISP) para contornar a falta de cabos.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema1" value="C" [(ngModel)]="modE_dilema1" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção C:</strong> Revender conexões caras de satélite estrangeiro exclusivamente para os 2 ou 3 maiores produtores rurais da região.</span>
                </label>
              </div>

              <div>
                <label class="block text-xs font-bold text-zinc-200 mt-2 mb-1 text-justify">
                  <strong>6. Análise da Decisão (Dilema 1):</strong> Com base na opção escolhida pelo seu grupo, quais <strong>duas CCEs</strong> sustentam essa estratégia? Justifique por que essa atitude representa uma postura empreendedora em redes:
                </label>
                <textarea [(ngModel)]="modE_q6" rows="3" placeholder="Sua justificativa CCE..." class="w-full bg-[#121214] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
              </div>
            </div>

            <!-- DILEMA 2 -->
            <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3">
              <h3 class="text-sm font-bold text-sky-400">🎮 DILEMA 2: O Aumento de Tráfego e o Gargalo Tecnológico (Ano de 2010)</h3>
              <p class="text-xs text-zinc-300 text-justify"><strong>Cenário:</strong> Sua rede sem fio já atende milhares de assinantes, mas a popularização de serviços pesados de streaming começa a saturar o sinal de rádio. Em dias chuvosos, ocorrem lentidão e reclamações.</p>

              <div class="space-y-2 text-xs text-zinc-300">
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema2" value="A" [(ngModel)]="modE_dilema2" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção A:</strong> Aplicar limites rígidos de consumo de dados (franquias baixas) para economizar banda e evitar novos investimentos.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema2" value="B" [(ngModel)]="modE_dilema2" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção B:</strong> Reinvestir grande parte do capital para construir uma rede própria de Fibra Óptica (FTTH) no semiárido, antecipando uma tecnologia de ponta na região.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema2" value="C" [(ngModel)]="modE_dilema2" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção C:</strong> Vender a carteira de clientes para um concorrente e encerrar a operação antes do esgotamento da rede.</span>
                </label>
              </div>

              <div>
                <label class="block text-xs font-bold text-zinc-200 mt-2 mb-1 text-justify">
                  <strong>7. Análise da Decisão (Dilema 2):</strong> Qual o impacto da opção escolhida pelo seu grupo a longo prazo? Quais CCEs dos conjuntos de <strong>Realização</strong> e <strong>Planejamento</strong> foram necessárias para bancar essa decisão?
                </label>
                <textarea [(ngModel)]="modE_q7" rows="3" placeholder="Sua justificativa..." class="w-full bg-[#121214] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
              </div>
            </div>

            <!-- DILEMA 3 -->
            <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3">
              <h3 class="text-sm font-bold text-sky-400">🎮 DILEMA 3: A Escassez de Especialistas Técnicos na Região</h3>
              <p class="text-xs text-zinc-300 text-justify"><strong>Cenário:</strong> A empresa precisa expandir milhares de quilômetros de rede, mas faltam profissionais formados em fusão de fibra óptica e infraestrutura de roteamento avançado na região.</p>

              <div class="space-y-2 text-xs text-zinc-300">
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema3" value="A" [(ngModel)]="modE_dilema3" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção A:</strong> Contratar equipes técnicas terceirizadas de grandes capitais pagando custos altos de deslocamento e repassando o valor na mensalidade.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema3" value="B" [(ngModel)]="modE_dilema3" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção B:</strong> Criar uma estrutura própria de capacitação técnica na empresa, forming jovens da própria comunidade local para assumirem os postos de trabalho.</span>
                </label>
                <label class="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="dilema3" value="C" [(ngModel)]="modE_dilema3" class="mt-0.5 accent-[#F27D26]">
                  <span><strong>Opção C:</strong> Interromper o plano de expansão e limitar a operação apenas às áreas onde já existem técnicos disponíveis.</span>
                </label>
              </div>

              <div>
                <label class="block text-xs font-bold text-zinc-200 mt-2 mb-1 text-justify">
                  <strong>8. Análise da Decisão (Dilema 3):</strong> Como a escolha do seu grupo se relaciona com as CCEs de <strong>Comprometimento</strong> e <strong>Planejamento e Monitoramento Sistemáticos</strong>?
                </label>
                <textarea [(ngModel)]="modE_q8" rows="3" placeholder="Sua justificativa..." class="w-full bg-[#121214] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- FULLSCREEN IMMERSIVE PRESENTATION OVERLAY MODAL (GAMMA APP / POWERPOINT STYLE) -->
      @if (isPresentationModalOpen()) {
        <div class="fixed inset-0 z-[100] bg-[#0A0A0B] backdrop-blur-2xl p-4 sm:p-8 flex flex-col justify-between select-none overflow-hidden h-screen w-screen max-h-screen max-w-screen animate-in fade-in zoom-in-95 duration-200">
          
          <!-- Top Bar inside Overlay -->
          <div class="max-w-6xl w-full mx-auto flex items-center justify-between pb-4 border-b border-white/10 text-white">
            <div class="flex items-center space-x-3">
              <span class="w-9 h-9 rounded-xl bg-[#F27D26]/20 border border-[#F27D26]/40 text-[#F27D26] font-bold flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">slideshow</span>
              </span>
              <div>
                <h3 class="text-sm font-bold text-white tracking-wide">Aula 3: As 10 CCEs e o Perfil Empreendedor em Redes</h3>
              </div>
            </div>
          </div>

          <!-- Centered 16:9 Presentation Canvas Container -->
          <div class="max-w-6xl w-full mx-auto my-auto bg-[#121214] border border-white/15 rounded-3xl p-6 sm:p-12 shadow-2xl shadow-black/90 min-h-[460px] sm:min-h-[540px] flex flex-col justify-center relative overflow-hidden">
            <div class="absolute top-0 right-0 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

            <!-- SLIDES CONTENT IN OVERLAY -->
            <div class="my-auto">
              <!-- SLIDE 1 -->
              @if (currentSlide() === 0) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">psychology</span>
                    <span>AULA 3 • COMPORTAMENTO EMPREENDEDOR</span>
                  </span>

                  <div>
                    <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                      O Perfil que Transforma Redes em Sucesso
                    </h1>
                    <p class="text-lg sm:text-xl text-zinc-400 font-medium mt-2">
                      Dominando as 10 Características do Comportamento Empreendedor (CCEs).
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 sm:p-8 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                    <h3 class="text-xl font-bold text-white flex items-center space-x-2">
                      <span>🙋‍♂️</span>
                      <span>A Jornada do João (Novo Desafio):</span>
                    </h3>
                    <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                      Na Aula 2, o João instalou o Link Duplo e organizou o cabeamento da <em>Logística Expressa</em>. A rede ficou perfeita! Mas o <strong>Sr. Roberto trouxe uma nova preocupação de negócio:</strong> <em class="text-amber-400 font-semibold">"João, a internet está ótima, mas e se nosso servidor em nuvem travar no meio da noite ou queimar um switch no fim de semana? Quem monitora isso para nós não pararmos?"</em>
                    </p>
                  </div>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    O cliente quer <strong>prevenção e tranquilidade contínua</strong>. Para transformar esse momento em um contrato mensal de longo prazo, o João precisará desenvolver seu <strong>Comportamento Empreendedor</strong>!
                  </p>
                </div>
              }

              <!-- SLIDE 2 -->
              @if (currentSlide() === 1) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">construction</span>
                    <span>PASSO 1 • CONCEITOS FUNDAMENTAIS</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Habilidades Técnicas vs. Habilidades Comportamentais
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    No mercado de TIC, todo profissional é avaliado sob duas perspectivas essenciais:
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-sky-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-sky-400 flex items-center space-x-2">
                        <span>🛠️</span>
                        <span>1. Habilidades Técnicas (Hard Skills)</span>
                      </h3>
                      <ul class="space-y-2 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>Configurar VLANs e roteadores MikroTik.</li>
                        <li>Montar e certificar cabeamento estruturado (Cat6/Fibra).</li>
                        <li>Implantar servidores Linux e regras de Firewall.</li>
                        <li class="text-sky-300 font-semibold"><strong>Papel:</strong> É a base técnica que qualifica você para atuar.</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-amber-400 flex items-center space-x-2">
                        <span>🧠</span>
                        <span>2. Habilidades Comportamentais (Soft Skills)</span>
                      </h3>
                      <ul class="space-y-2 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>Cumprir prazos com rigor e ter iniciativa própria.</li>
                        <li>Ouvir as necessidades do cliente sem impor jargões técnicos.</li>
                        <li>Manter o equilíbrio emocional e resolver crises com agilidade.</li>
                        <li class="text-amber-300 font-semibold"><strong>Papel:</strong> É o que constrói reputação, contratos duradouros e fidelidade.</li>
                      </ul>
                    </div>
                  </div>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-4 border border-[#F27D26]/40 text-center font-mono text-sm sm:text-base font-bold text-[#F27D26] leading-relaxed shadow-lg">
                    REGRA DO MERCADO DE TI: O conhecimento técnico abre a porta, mas o comportamento empreendedor mantém a porta aberta! 🔑
                  </div>
                </div>
              }

              <!-- SLIDE 3 -->
              @if (currentSlide() === 2) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">science</span>
                    <span>PASSO 2 • METODOLOGIA CIENTÍFICA</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O que é CCE? A Metodologia McClelland (Sebrae/ONU)
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed text-justify">
                    A sigla <strong>CCE</strong> significa <strong>Características do Comportamento Empreendedor</strong>.
                  </p>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3">
                    <p class="text-xs sm:text-base text-zinc-300 leading-relaxed text-justify">
                      Na década de 1980, o psicólogo <strong>David McClelland</strong> (da Universidade de Harvard), em parceria com a <strong>ONU</strong> (e aplicada no Brasil pelo <strong>SEBRAE</strong> em programas como Empretec e Despertar), estudou empreendedores de sucesso em mais de 30 países para descobrir o segredo do sucesso nos negócios.
                    </p>
                  </div>

                  <div class="bg-emerald-950/50 rounded-2xl p-6 border border-emerald-500/40 space-y-2 shadow-xl">
                    <h3 class="text-base sm:text-lg font-bold text-emerald-300 flex items-center space-x-2">
                      <span class="material-symbols-outlined text-emerald-400 text-xl">verified</span>
                      <span>A Grande Descoberta Científica:</span>
                    </h3>
                    <p class="text-zinc-200 text-sm sm:text-base leading-relaxed text-justify">
                      Empreendedorismo <strong>NÃO é sorte nem dom divino de nascimento!</strong> Trata-se de um conjunto de <strong>10 comportamentos observáveis e mensuráveis</strong> que qualquer profissional pode treinar e aprimorar para alcançar excelência em sua carreira técnica.
                    </p>
                  </div>
                </div>
              }

              <!-- SLIDE 4 -->
              @if (currentSlide() === 3) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">grid_view</span>
                    <span>PASSO 3 • O MAPA GERAL DAS 10 CCES</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Os 3 Conjuntos do Comportamento Empreendedor
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-3">
                      <div class="text-3xl">⚡</div>
                      <h3 class="font-bold text-[#F27D26] text-base">1. Conjunto de Realização</h3>
                      <ul class="space-y-1.5 text-xs sm:text-sm text-zinc-300 font-mono">
                        <li>• CCE 1: Busca de Oportunidades</li>
                        <li>• CCE 2: Riscos Calculados</li>
                        <li>• CCE 3: Qualidade & Eficiência</li>
                        <li>• CCE 4: Persistência</li>
                        <li>• CCE 5: Comprometimento</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-3">
                      <div class="text-3xl">🎯</div>
                      <h3 class="font-bold text-sky-400 text-base">2. Conjunto de Planejamento</h3>
                      <ul class="space-y-1.5 text-xs sm:text-sm text-zinc-300 font-mono">
                        <li>• CCE 6: Busca de Informações</li>
                        <li>• CCE 7: Estabelecimento de Metas</li>
                        <li>• CCE 8: Planejamento e Monitoramento Sistemáticos</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-3">
                      <div class="text-3xl">🤝</div>
                      <h3 class="font-bold text-purple-400 text-base">3. Conjunto de Poder</h3>
                      <ul class="space-y-1.5 text-xs sm:text-sm text-zinc-300 font-mono">
                        <li>• CCE 9: Persuasão & Networking</li>
                        <li>• CCE 10: Autoconfiança & Independência</li>
                      </ul>
                    </div>
                  </div>

                  <div class="bg-indigo-950/40 rounded-2xl p-4 border border-indigo-500/30 text-xs sm:text-sm text-zinc-300 text-center font-mono">
                    💡 A seguir, veja como aplicar cada uma das 10 CCEs no dia a dia de Redes de Computadores!
                  </div>
                </div>
              }

              <!-- SLIDE 5 -->
              @if (currentSlide() === 4) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">bolt</span>
                    <span>CONJUNTO 1 • BLOCO DE REALIZAÇÃO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    As 5 CCEs de Realização em Redes
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <span class="px-2.5 py-1 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold text-xs">CCE 1 & 2</span>
                      <h3 class="font-bold text-white text-base">Iniciativa & Riscos Calculados</h3>
                      <p class="text-zinc-300 text-justify">Agir antes de ser chamado (CCE 1) e fazer backup preventivo de segurança antes de atualizar firmware de roteadores corporativos (CCE 2).</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <span class="px-2.5 py-1 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold text-xs">CCE 3</span>
                      <h3 class="font-bold text-white text-base">Qualidade e Eficiência</h3>
                      <p class="text-zinc-300 text-justify">Identificar cabos com anilhas térmicas, organizar o rack de servidor e testar todas as conexões com relatórios de certificação.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2 md:col-span-2">
                      <span class="px-2.5 py-1 rounded bg-[#F27D26]/20 text-[#F27D26] font-mono font-bold text-xs">CCE 4 & 5</span>
                      <h3 class="font-bold text-white text-base">Persistência & Comprometimento</h3>
                      <p class="text-zinc-300 text-justify">Analisar pacotes e logs repetidamente até achar a causa de uma lentidão intermitente (CCE 4) e assumir a responsabilidade pessoal de restabelecer o sinal rompido do cliente (CCE 5).</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 6 -->
              @if (currentSlide() === 5) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">timeline</span>
                    <span>CONJUNTO 2 • BLOCO DE PLANEJAMENTO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    As 3 CCEs de Planejamento em Redes
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-5 text-xs sm:text-sm">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-sky-500/30 space-y-3">
                      <span class="px-2.5 py-1 rounded bg-sky-500/20 text-sky-400 font-mono font-bold text-xs">CCE 6</span>
                      <h3 class="font-bold text-white text-base">Busca de Informações</h3>
                      <p class="text-zinc-300 leading-relaxed text-justify">Pesquisar a fundo relatórios de segurança da Anatel/Cert.br, consultar documentações técnicas de fabricantes e mapear o tráfego do cliente antes de orçar.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-sky-500/30 space-y-3">
                      <span class="px-2.5 py-1 rounded bg-sky-500/20 text-sky-400 font-mono font-bold text-xs">CCE 7</span>
                      <h3 class="font-bold text-white text-base">Estabelecimento de Metas</h3>
                      <p class="text-zinc-300 leading-relaxed text-justify">Definir metas claras e mensuráveis. Ex: <em>"Vou conquistar 8 contratos de suporte gerenciado (MSP) em 6 meses, faturando R$ 5.200,00 mensais."</em></p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-sky-500/30 space-y-3">
                      <span class="px-2.5 py-1 rounded bg-sky-500/20 text-sky-400 font-mono font-bold text-xs">CCE 8</span>
                      <h3 class="font-bold text-white text-base">Planejamento & Monitoramento</h3>
                      <p class="text-zinc-300 leading-relaxed text-justify">Dividir implantações grandes em etapas no Kanban, acompanhar cronogramas e monitorar a disponibilidade (SLA 99.5%) em painéis do Zabbix.</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 7 -->
              @if (currentSlide() === 6) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">handshake</span>
                    <span>CONJUNTO 3 • BLOCO DE PODER</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    As 2 CCEs de Poder e Relacionamento em TIC
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-purple-500/40 space-y-3 shadow-xl">
                      <span class="px-2.5 py-1 rounded bg-purple-500/20 text-purple-400 font-mono font-bold text-xs">CCE 9</span>
                      <h3 class="text-base sm:text-lg font-bold text-white flex items-center space-x-2">
                        <span>🗣️</span>
                        <span>Persuasão e Rede de Contatos (Networking)</span>
                      </h3>
                      <ul class="space-y-2 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>Saber explicar conceitos (Failover, VLAN, SLA) em linguagem simples de negócios.</li>
                        <li>Cultivar bom relacionamento com professores, colegas e empresários locais.</li>
                        <li class="text-purple-300 font-semibold">Fato de mercado: Mais de 75% dos contratos em TI surgem por indicação direta!</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-purple-500/40 space-y-3 shadow-xl">
                      <span class="px-2.5 py-1 rounded bg-purple-500/20 text-purple-400 font-mono font-bold text-xs">CCE 10</span>
                      <h3 class="text-base sm:text-lg font-bold text-white flex items-center space-x-2">
                        <span>🦁</span>
                        <span>Independência e Autoconfiança</span>
                      </h3>
                      <ul class="space-y-2 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>Confiar no seu conhecimento técnico e saber precificar seu trabalho de forma justa.</li>
                        <li>Não aceitar valores humilhantes por medo de perder o serviço.</li>
                        <li class="text-purple-300 font-semibold">Manter sua recomendação de segurança mesmo quando o cliente pede improvisos arriscados.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 8 -->
              @if (currentSlide() === 7) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">cases</span>
                    <span>ESTUDO DE CASO PRÁTICO • APLICAÇÃO REAL</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Como o João Respondeu ao Sr. Roberto
                  </h2>

                  <div class="bg-[#0A0A0B] rounded-2xl p-5 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2 shadow-xl">
                    <p class="text-xs sm:text-base text-zinc-300 leading-relaxed text-justify">
                      Veja como o João utilizou as CCEs para transformar a dúvida do Sr. Roberto sobre falhas noturnas em uma oportunidade comercial:
                    </p>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                    <div class="bg-[#0A0A0B] rounded-2xl p-4 border border-white/10 space-y-2">
                      <h3 class="font-bold text-[#F27D26]">1. Busca de Informações & Planejamento (CCE 6 e 8)</h3>
                      <p class="text-zinc-300 text-justify">João analisou o fluxo da Logística Expressa e configurou um painel Zabbix com alertas automáticos no Telegram para avisar antes do servidor travar.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-4 border border-white/10 space-y-2">
                      <h3 class="font-bold text-[#F27D26]">2. Qualidade & Comprometimento (CCE 3 e 5)</h3>
                      <p class="text-zinc-300 text-justify">Definiu um Acordo de Nível de Serviço (SLA) por escrito: caso ocorra falha crítica, o atendimento presencial é garantido em até 40 minutos.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-4 border border-white/10 space-y-2">
                      <h3 class="font-bold text-[#F27D26]">3. Iniciativa & Riscos Calculados (CCE 1 e 2)</h3>
                      <p class="text-zinc-300 text-justify">Estruturou rotinas automatizadas de backup diário na nuvem com testes semanais de restauração para garantir zero perda de dados.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-4 border border-white/10 space-y-2">
                      <h3 class="font-bold text-[#F27D26]">4. Persuasão & Autoconfiança (CCE 9 e 10)</h3>
                      <p class="text-zinc-300 text-justify">Apresentou a proposta de <strong>Contrato Mensal MSP por R$ 650/mês</strong>, demonstrando que o valor garante a operação contínua da empresa.</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 9 -->
              @if (currentSlide() === 8) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">autorenew</span>
                    <span>MODELO DE NEGÓCIO • SUSTENTABILIDADE</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Do "Técnico Apaga-Fogo" ao Negócio MSP
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-red-500/40 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-red-400 flex items-center space-x-2">
                        <span>🔥</span>
                        <span>Modelo Antigo ("Quebra-Paga" / Apaga-Fogo)</span>
                      </h3>
                      <ul class="space-y-2.5 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>Você só ganha dinheiro quando o cliente está em desespero e prejuízo.</li>
                        <li>Renda instável: num mês fatura R$ 2.000, no outro apenas R$ 300.</li>
                        <li>Você passa o dia correndo atrás de chamados desorganizados.</li>
                        <li>O cliente enxerga você como um "custo chato".</li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-emerald-500/40 space-y-3 shadow-xl">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400 flex items-center space-x-2">
                        <span>💎</span>
                        <span>Modelo MSP (Serviços Gerenciados com SLA)</span>
                      </h3>
                      <ul class="space-y-2.5 text-xs sm:text-sm text-zinc-300 pl-4 list-disc text-justify">
                        <li>O cliente paga uma mensalidade fixa para você <strong>prevenir falhas</strong>.</li>
                        <li>Renda previsível garantida todo dia 5 do mês.</li>
                        <li>Ferramentas automatizadas para monitorar múltiplos clientes ao mesmo tempo.</li>
                        <li>O cliente enxerga você como um parceiro estratégico essencial.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 10 -->
              @if (currentSlide() === 9) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">trending_up</span>
                    <span>METAS FINANCEIRAS • ESCALABILIDADE</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Matemática da Liberdade Financeira em TIC
                  </h2>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-4 border border-[#F27D26]/40 text-center font-mono text-sm sm:text-base font-bold text-[#F27D26] leading-relaxed shadow-lg">
                    MENSALIDADE POR CONTRATO (R$ 650,00) × QUANTIDADE DE CLIENTES = RECURSO RECORRENTE 📈
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <h3 class="text-xs sm:text-sm font-bold text-zinc-400">Fase 1: Início (1 a 3 meses)</h3>
                      <p class="text-2xl font-bold text-[#F27D26]">3 Contratos</p>
                      <p class="text-sm font-bold text-white">R$ 1.950,00 / mês</p>
                      <p class="text-xs text-zinc-400">Cobre seus custos de transporte, internet e ferramentas.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-[#F27D26]/60 space-y-2 shadow-xl shadow-[#F27D26]/10">
                      <h3 class="text-xs sm:text-sm font-bold text-[#F27D26]">Fase 2: Consolidação (6 meses)</h3>
                      <p class="text-2xl font-bold text-white">8 Contratos</p>
                      <p class="text-sm font-bold text-[#F27D26]">R$ 5.200,00 / mês</p>
                      <p class="text-xs text-zinc-300">Renda superior ao piso salarial médio de técnicos contratados.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-emerald-500/50 space-y-2">
                      <h3 class="text-xs sm:text-sm font-bold text-emerald-400">Fase 3: Expansão (12 meses)</h3>
                      <p class="text-2xl font-bold text-emerald-400">15 Contratos</p>
                      <p class="text-sm font-bold text-white">R$ 9.750,00 / mês</p>
                      <p class="text-xs text-zinc-400">Hora de contratar um estagiário de redes e abrir escritório!</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 11 -->
              @if (currentSlide() === 10) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">forum</span>
                    <span>PERSUASÃO • NEGOCIAÇÃO DE VALOR</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Como Vencer as Objeções dos Clientes
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-sm sm:text-base font-bold text-red-400">Objeção 1: <em>"Se a rede não der defeito no mês, vou pagar sem usar?"</em></h3>
                      <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
                        <strong>Resposta Empreendedora:</strong><br>
                        <em>"Sr. Roberto, pense no plano de saúde ou no seguro do carro: pagamos torcendo para não usar! Na TI, a ausência de problemas é o resultado do nosso monitoramento diário e limpezas preventivas, garantindo que o senhor nunca perca R$ 1.500/hora em paralisações."</em>
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3 shadow-xl">
                      <h3 class="text-sm sm:text-base font-bold text-red-400">Objeção 2: <em>"Achei R$ 650 caro, faz por R$ 200?"</em></h3>
                      <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
                        <strong>Resposta Empreendedora (Autoconfiança CCE 10):</strong><br>
                        <em>"Entendo sua preocupação de custos. Porém, por R$ 200 não é possível manter servidor de monitoramento 24h na nuvem nem garantir SLA de atendimento presencial em 40 minutos por contrato. Nosso valor garante a segurança total da sua empresa."</em>
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 12 -->
              @if (currentSlide() === 11) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">verified</span>
                    <span>SÍNTESE DA AULA</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Grande Lição do Dia
                  </h2>

                  <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-10 border border-[#F27D26]/50 text-center shadow-2xl">
                    <h3 class="text-xl sm:text-2xl lg:text-3xl font-extrabold text-[#F27D26] leading-relaxed">
                      "O conhecimento técnico em redes ensina você a configurar switches e servidores.<br><br>As Características do Comportamento Empreendedor (CCEs) ensinam você a ser dono do seu próprio destino e construir uma carreira de respeito."
                    </h3>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-4 shadow-xl">
                    <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                      <span>🚀</span>
                      <span>Próximo Passo:</span>
                    </h3>
                    <p class="text-zinc-300 text-xs sm:text-base leading-relaxed text-justify">
                      Abra a <strong>Folha de Atividades</strong>, realize o Teste de Autoavaliação Oficial das 10 CCEs (com pontuação automática Sebrae/ONU), calcule seu nível empreendedor, estruture o MSP e enfrente o desafio real da Brisanet no Módulo E!
                    </p>
                    <div>
                      <button 
                        type="button" 
                        (click)="closePresentationModal(); onSelectView('activity')" 
                        class="px-5 py-2.5 rounded-xl bg-[#F27D26] text-black font-bold text-xs sm:text-sm hover:bg-[#e06c17] transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-[#F27D26]/20">
                        <span class="material-symbols-outlined text-base">assignment</span>
                        <span>Ir para Folha de Atividades Completa</span>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Bottom Floating Control Dock (Gamma Style) -->
          <div class="max-w-3xl w-full mx-auto bg-[#121214]/95 backdrop-blur border border-white/15 rounded-2xl p-2.5 shadow-2xl flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:space-x-3">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span class="hidden sm:inline">Anterior</span>
            </button>

            <!-- Interactive Slide Jump Buttons -->
            <div class="flex items-center justify-center gap-1 flex-wrap">
              @for (slideNum of slideNumbers; track slideNum) {
                <button 
                  type="button" 
                  (click)="goToSlide(slideNum)"
                  [class]="currentSlide() === slideNum 
                    ? 'bg-[#F27D26] text-black font-bold scale-110 shadow-md shadow-[#F27D26]/30' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'"
                  class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg text-[11px] font-mono transition-all flex items-center justify-center cursor-pointer">
                  {{ slideNum + 1 }}
                </button>
              }
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-3.5 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer shrink-0">
              <span class="hidden sm:inline">Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }
    </section>
  `
})
export class Aula3SectionComponent {
  navigateBack = output<void>();

  activeView = signal<'slides' | 'activity'>('slides');
  currentSlide = signal<number>(0);
  totalSlides = 12;
  slideNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  isPresentationModalOpen = signal<boolean>(false);

  // Módulo A Model
  modA_assoc: string[] = Array(10).fill('');

  // Módulo B Scores & Calculation
  cceScores = {
    q1: 4,
    q2: 4,
    q3: 5,
    q4: 4,
    q5: 5,
    q6: 4,
    q7: 4,
    q8: 4,
    q9: 3,
    q10: 4
  };

  totalScore = signal<number>(41);
  modB_autoanalise = '';

  // Módulo C
  modC_q3 = '';
  modC_q4_a = 'R$ 6.500,00 / mês';
  modC_q4_b = 'R$ 6.050,00 / mês (R$ 6.500 - R$ 450)';

  // Módulo D
  modD_pitch = '';

  // Módulo E (RPG)
  modE_dilema1 = 'B';
  modE_q6 = '';
  modE_dilema2 = 'B';
  modE_q7 = '';
  modE_dilema3 = 'B';
  modE_q8 = '';

  recalcTotal() {
    const scores = Object.values(this.cceScores).map(v => Number(v) || 0);
    const sum = scores.reduce((acc, curr) => acc + curr, 0);
    this.totalScore.set(sum);
  }

  onSelectView(view: 'slides' | 'activity') {
    this.activeView.set(view);
    if (view === 'slides') {
      this.openPresentationModal();
    }
  }

  nextSlide() {
    if (this.currentSlide() < this.totalSlides - 1) {
      this.currentSlide.update(v => v + 1);
    }
  }

  prevSlide() {
    if (this.currentSlide() > 0) {
      this.currentSlide.update(v => v - 1);
    }
  }

  goToSlide(slideIndex: number) {
    if (slideIndex >= 0 && slideIndex < this.totalSlides) {
      this.currentSlide.set(slideIndex);
    }
  }

  openPresentationModal() {
    this.isPresentationModalOpen.set(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }
  }

  closePresentationModal() {
    this.isPresentationModalOpen.set(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }
  }

  printActivity() {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isPresentationModalOpen() || this.activeView() === 'slides') {
      if (event.key === 'ArrowRight' || event.key === ' ') {
        this.nextSlide();
      } else if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'Escape') {
        this.closePresentationModal();
      }
    }
  }
}
